import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

@Component({
  selector: 'app-line-chart',
  standalone: true,
  imports: [],
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss'
})
export class LineChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  @ViewChild('chartContainer') chartContainer!: ElementRef<HTMLElement>;
  private myChart!: echarts.ECharts;
  private viewReady = false;

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService
  ) {
    this.globalFilterService.filter$.subscribe((filters) => {
      if (Object.keys(filters).length !== 0) {
        this.loadAndRenderChart(filters);
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.loadAndRenderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.viewReady) {
      this.loadAndRenderChart();
    }
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = this.chartContainer?.nativeElement;
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    const staticData = this.data?.chartData || this.data?.staticData;
    if (staticData) {
      this.renderChart(staticData);
      this.addResizeListener();
      return;
    }

    if (!this.data?.apiUrl) {
      this.renderChart();
      this.addResizeListener();
      return;
    }

    this.configService.getConfig(this.data.apiUrl, filters).subscribe({
      next: (res) => {
        this.renderChart(res?.data || res);
        this.addResizeListener();
      },
      error: () => {
        this.renderChart();
      }
    });
  }

  private renderChart(chartData?: { category?: string[]; value?: number[]; series?: any[]; legend?: string[] }): void {
    const series = Array.isArray(chartData?.series) && chartData.series.length
      ? chartData.series
      : [
          {
            type: 'line',
            data: chartData?.value || [],
            smooth: true,
            lineStyle: { width: 2 },
            itemStyle: { color: '#91cc75' }
          }
        ];

    const option = {
      tooltip: { trigger: 'axis' },
      legend: chartData?.legend?.length ? { data: chartData.legend, right: 8, top: 0 } : undefined,
      grid: this.data?.grid || { left: 35, right: 20, top: 34, bottom: 28 },
      xAxis: {
        type: 'category',
        data: chartData?.category || []
      },
      yAxis: {
        type: 'value'
      },
      series
    };

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
  }

  private addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
